<template>
  <el-dialog
    :title="$t('otaManagement.downloadTitle') || '固件下载'"
    :visible.sync="visible"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="status !== 'downloading'"
    @close="handleClose"
  >
    <div class="download-content">
      <div class="file-info">
        <div class="info-item">
          <span class="label">{{ $t('otaManagement.firmwareName') || '固件名称' }}:</span>
          <span class="value">{{ firmware.firmwareName }}</span>
        </div>
        <div class="info-item">
          <span class="label">{{ $t('otaManagement.version') || '版本号' }}:</span>
          <span class="value">{{ firmware.version }}</span>
        </div>
        <div class="info-item">
          <span class="label">{{ $t('otaManagement.fileSize') || '文件大小' }}:</span>
          <span class="value">{{ formatSize(totalSize) }}</span>
        </div>
      </div>

      <div class="progress-section">
        <el-progress 
          :percentage="progress" 
          :status="progressStatus"
          :stroke-width="20"
          text-inside
        ></el-progress>
        <div class="progress-details">
          <span>{{ formatSize(downloadedSize) }} / {{ formatSize(totalSize) }}</span>
          <span>{{ speed }} /s</span>
        </div>
        <div class="time-remaining" v-if="status === 'downloading'">
          {{ $t('otaManagement.remainingTime') || '剩余时间' }}: {{ remainingTime }}
        </div>
      </div>

      <div v-if="errorMsg" class="error-msg">
        <i class="el-icon-error"></i> {{ errorMsg }}
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <template v-if="status === 'downloading'">
        <el-button type="warning" size="small" @click="pauseDownload">
          {{ $t('common.pause') || '暂停' }}
        </el-button>
        <el-button type="danger" size="small" @click="cancelDownload">
          {{ $t('common.cancel') || '取消' }}
        </el-button>
      </template>
      
      <template v-else-if="status === 'paused'">
        <el-button type="primary" size="small" @click="resumeDownload">
          {{ $t('common.resume') || '继续' }}
        </el-button>
        <el-button size="small" @click="cancelDownload">
          {{ $t('common.cancel') || '取消' }}
        </el-button>
      </template>

      <template v-else-if="status === 'failed'">
        <el-button type="primary" size="small" @click="retryDownload">
          {{ $t('common.retry') || '重试' }}
        </el-button>
        <el-button size="small" @click="handleClose">
          {{ $t('common.close') || '关闭' }}
        </el-button>
      </template>

      <template v-else-if="status === 'completed'">
        <el-button type="success" size="small" @click="handleClose">
          {{ $t('common.completed') || '完成' }}
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import request from '@/utils/request'
import { getToken } from '@/utils/auth'
import axios from 'axios'

export default {
  name: 'DownloadDialog',
  data() {
    return {
      visible: false,
      firmware: {},
      status: 'idle', // idle, downloading, paused, completed, failed
      progress: 0,
      downloadedSize: 0,
      totalSize: 0,
      speed: '0 B',
      remainingTime: '--:--',
      errorMsg: '',
      
      // 下载控制
      cancelTokenSource: null,
      startTime: 0,
      lastDownloadedSize: 0,
      lastUpdateTime: 0,
      chunks: [], // 用于存储已下载的数据块
    }
  },
  computed: {
    progressStatus() {
      if (this.status === 'completed') return 'success'
      if (this.status === 'failed') return 'exception'
      if (this.status === 'paused') return 'warning'
      return null
    }
  },
  methods: {
    show(firmware) {
      this.firmware = firmware
      this.visible = true
      this.reset()
      this.startDownload()
    },
    reset() {
      this.status = 'idle'
      this.progress = 0
      this.downloadedSize = 0
      this.totalSize = this.firmware.size || 0
      this.speed = '0 B'
      this.remainingTime = '--:--'
      this.errorMsg = ''
      this.chunks = []
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel()
      }
    },
    async startDownload() {
      this.status = 'downloading'
      this.startTime = Date.now()
      this.lastUpdateTime = this.startTime
      this.lastDownloadedSize = 0
      this.errorMsg = ''

      try {
        // 1. 获取下载 UUID
        console.log('Fetching download UUID for firmware:', this.firmware.id)
        const urlRes = await request({
          url: `/otaMag/download/url/${this.firmware.id}`,
          method: 'get'
        })

        console.log('Received UUID response:', urlRes)

        // request 拦截器已经处理了 Result 包装，如果成功直接返回 Result.data
        const uuid = urlRes
        if (!uuid) {
          throw new Error('获取下载地址失败：UUID为空')
        }

        // 2. 开始流式下载
        this.cancelTokenSource = axios.CancelToken.source()
        
        // 使用项目的 request 工具进行流式下载
        const response = await request({
          url: `/otaMag/ota-download/${uuid}`,
          method: 'get',
          responseType: 'blob',
          cancelToken: this.cancelTokenSource.token,
          onDownloadProgress: (progressEvent) => {
            this.handleProgress(progressEvent)
          }
        })
        console.log('Download request successful, size:', response.size || (response.data && response.data.size))

        // 下载完成
        this.status = 'completed'
        this.progress = 100
        this.saveFile(response.data || response)
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Download canceled')
        } else {
          console.error('Download error full object:', error)
          if (error.response) {
            console.error('Error data:', error.response.data)
            console.error('Error status:', error.response.status)
          }
          this.handleError(error)
        }
      }
    },
    handleProgress(progressEvent) {
      if (!progressEvent.lengthComputable && !this.totalSize) return
      
      const now = Date.now()
      const duration = (now - this.lastUpdateTime) / 1000 // 秒
      
      this.downloadedSize = progressEvent.loaded
      if (progressEvent.total) {
        this.totalSize = progressEvent.total
      }

      this.progress = Math.round((this.downloadedSize / this.totalSize) * 100)

      // 每隔 500ms 更新一次速度和剩余时间
      if (duration >= 0.5) {
        const newlyDownloaded = this.downloadedSize - this.lastDownloadedSize
        const currentSpeed = newlyDownloaded / duration // B/s
        this.speed = this.formatSize(currentSpeed)
        
        if (currentSpeed > 0) {
          const remainingBytes = this.totalSize - this.downloadedSize
          const remainingSeconds = Math.round(remainingBytes / currentSpeed)
          this.remainingTime = this.formatTime(remainingSeconds)
        }

        this.lastUpdateTime = now
        this.lastDownloadedSize = this.downloadedSize
      }
    },
    handleError(error) {
      this.status = 'failed'
      this.errorMsg = error.message || '下载过程中发生错误'
      console.error('Download error:', error)
    },
    pauseDownload() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('paused')
        this.status = 'paused'
      }
    },
    resumeDownload() {
      // 注意：标准的 axios 不支持真正的暂停/恢复（断点续传）
      // 除非后端支持 Range 请求。这里我们先实现重新开始，
      // 如果需要真正的断点续传，需要更复杂的实现（分片请求）。
      // 为简化演示，这里先实现重新开始。
      this.startDownload()
    },
    cancelDownload() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('canceled')
      }
      this.visible = false
      this.reset()
    },
    retryDownload() {
      this.reset()
      this.startDownload()
    },
    saveFile(blob) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      
      // 尝试从路径中获取文件名
      let filename = this.firmware.firmwareName || 'firmware'
      if (this.firmware.firmwarePath && this.firmware.firmwarePath.includes('.')) {
        const ext = this.firmware.firmwarePath.split('.').pop()
        filename += `.${ext}`
      } else {
        filename += '.bin'
      }

      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    handleClose() {
      if (this.status === 'downloading') {
        this.$confirm('正在下载中，确定取消吗？', '提示', {
          type: 'warning'
        }).then(() => {
          this.cancelDownload()
        }).catch(() => {
          this.visible = true
        })
      } else {
        this.visible = false
      }
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatTime(seconds) {
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      return [h, m, s]
        .map(v => v < 10 ? '0' + v : v)
        .filter((v, i) => v !== '00' || i > 0)
        .join(':')
    }
  }
}
</script>

<style scoped lang="scss">
.download-content {
  .file-info {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    margin-bottom: 20px;

    .info-item {
      margin-bottom: 8px;
      &:last-child { margin-bottom: 0; }
      
      .label {
        color: #606266;
        margin-right: 10px;
        font-weight: bold;
      }
      .value {
        color: #303133;
      }
    }
  }

  .progress-section {
    margin-bottom: 20px;

    .progress-details {
      display: flex;
      justify-content: space-between;
      margin-top: 10px;
      color: #909399;
      font-size: 13px;
    }

    .time-remaining {
      margin-top: 5px;
      text-align: right;
      color: #E6A23C;
      font-size: 13px;
    }
  }

  .error-msg {
    color: #F56C6C;
    background-color: #fef0f0;
    padding: 10px;
    border-radius: 4px;
    font-size: 14px;
    display: flex;
    align-items: center;
    
    i { margin-right: 8px; font-size: 18px; }
  }
}
</style>
